import { useEffect, useState } from 'react';
import { LifeBuoy } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { AttachmentPicker } from '@/components/ui/AttachmentPicker';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import {
  MAX_TICKET_ATTACHMENT_MB,
  MAX_TICKET_ATTACHMENTS,
  TICKET_ATTACHMENT_LABEL,
  TICKET_ATTACHMENT_TYPES,
  TICKET_CATEGORY_OPTIONS,
  TICKET_PRIORITY_OPTIONS,
} from '@/constants/support';
import { ApiRequestError } from '@/lib/apiClient';
import type { TicketSummary } from '@/types/support';
import { supportApi } from '../api/support.api';
import { newTicketSchema } from '../schemas/support.schema';

interface Draft {
  subject: string;
  category: string;
  priority: string;
  message: string;
}

type FieldErrors = Partial<Record<keyof Draft, string>>;

const EMPTY: Draft = { subject: '', category: '', priority: 'NORMAL', message: '' };

export interface NewTicketDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (ticket: TicketSummary) => void;
}

/**
 * Asking our support team for help.
 *
 * The draft survives a failed send and is cleared only once the ticket exists.
 */
export function NewTicketDialog({ isOpen, onClose, onCreated }: NewTicketDialogProps) {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (isOpen) setFormError(null);
  }, [isOpen]);

  function update(field: keyof Draft, value: string) {
    setDraft((current) => ({ ...current, [field]: value }));
    if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }));
  }

  function close() {
    if (!isSending) onClose();
  }

  async function submit() {
    const parsed = newTicketSchema.safeParse(draft);
    if (!parsed.success) {
      const fields: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof Draft;
        if (!fields[field]) fields[field] = issue.message;
      }
      setErrors(fields);
      return;
    }

    setIsSending(true);
    setFormError(null);
    try {
      const ticket = await supportApi.create(parsed.data, files);
      setDraft(EMPTY);
      setFiles([]);
      onCreated(ticket);
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        const fields: FieldErrors = {};
        for (const { field, message } of caught.fields) {
          if (field in EMPTY) fields[field as keyof Draft] = message;
        }
        setErrors(fields);
        setFormError(caught.message);
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSending(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      size="md"
      title="New support ticket"
      description="Tell us what is wrong. Our support team replies here, and by email."
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={isSending}>
            Cancel
          </Button>
          <Button
            leftIcon={<LifeBuoy className="h-4 w-4" aria-hidden />}
            isLoading={isSending}
            onClick={() => void submit()}
          >
            Raise ticket
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {formError && (
          <Alert tone="critical" title="Ticket not raised">
            {formError}
          </Alert>
        )}
        <Input
          label="Subject"
          placeholder="Fee receipts print as a blank page"
          maxLength={150}
          value={draft.subject}
          error={errors.subject}
          onChange={(event) => update('subject', event.target.value)}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="What is it about?"
            placeholder="Choose one"
            options={TICKET_CATEGORY_OPTIONS}
            value={draft.category}
            error={errors.category}
            onChange={(event) => update('category', event.target.value)}
          />
          <Select
            label="How urgent?"
            options={TICKET_PRIORITY_OPTIONS}
            value={draft.priority}
            error={errors.priority}
            hint="Urgent is for anything stopping the school working today."
            onChange={(event) => update('priority', event.target.value)}
          />
        </div>
        <Textarea
          label="Describe the problem"
          rows={6}
          maxLength={5000}
          placeholder="What were you doing, what did you expect, and what happened instead?"
          value={draft.message}
          error={errors.message}
          onChange={(event) => update('message', event.target.value)}
        />
        <AttachmentPicker
          label="Screenshots or files (optional)"
          files={files}
          onChange={setFiles}
          acceptedTypes={TICKET_ATTACHMENT_TYPES}
          acceptedLabel={TICKET_ATTACHMENT_LABEL}
          maxFiles={MAX_TICKET_ATTACHMENTS}
          maxFileMb={MAX_TICKET_ATTACHMENT_MB}
          disabled={isSending}
        />
      </form>
    </Modal>
  );
}
